import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { App as AntApp, Dropdown } from 'antd';
import AppBar from '../components/AppBar';
import DocViewer from '../components/DocViewer';
import { PdfMark } from '../components/PayoutSheet';
import useVoucherViewer from '../hooks/useVoucherViewer';
import PeriodPicker, { presetRange } from '../components/PeriodPicker';
import { Skeletons, RM, RM0 } from '../components/kit';
import { shortAddress } from '../lib/format';
import { buildStatementHtml, statementFileBase, statementRef, docDate } from '../lib/statementDoc';
import * as earnApi from '../api/earnings';
import * as lettersApi from '../api/coAgencyLetters';

// FULL-SCREEN · /earnings/statement?from=&to= — the caller's commission statement.
//
// Two renderings of one document, because they have different jobs:
//   · this screen  — a statement laid out for a 390px phone: period picker at the
//     top, a reconciliation an agent can read at arm's length, then itemised
//     sections as rows rather than a table squeezed sideways.
//   · the PDF      — src/lib/statementDoc.js builds the real company document
//     (HQ letterhead, A4 tables, notes) and DocViewer prints it. Printing THIS
//     screen was the old bug: the paper inherited the phone column and clipped.
//
// The period lives in the URL, so a statement can be linked to and reloaded.

const paramsOf = (sp) => {
  const month = sp.get('month');
  // ?month=YYYY-MM (from a payout row) is expanded to a range so the picker,
  // the CSV and the document all speak the same from/to.
  if (month && /^\d{4}-\d{2}$/.test(month)) {
    const [y, m] = month.split('-').map(Number);
    const last = new Date(y, m, 0).getDate();
    return { from: `${month}-01`, to: `${month}-${String(last).padStart(2, '0')}` };
  }
  return { from: sp.get('from') || null, to: sp.get('to') || null };
};

export default function StatementPreviewPage() {
  const [sp, setSp] = useSearchParams();
  const { message } = AntApp.useApp();
  const [st, setSt] = useState(null);
  const [err, setErr] = useState(false);
  const [docHtml, setDocHtml] = useState(null);
  const [busy, setBusy] = useState(false);
  const pv = useVoucherViewer();

  // Seed from the URL; default to this year, matching the Earnings hero.
  const [period, setPeriod] = useState(() => {
    const p = paramsOf(sp);
    return p.from || p.to ? p : presetRange('This year');
  });

  const query = useMemo(
    () => ({ ...(period?.from ? { from: period.from } : {}), ...(period?.to ? { to: period.to } : {}) }),
    [period]
  );

  // Keep the URL in step so the page can be shared, reloaded or opened again from
  // Earnings on the same period. replace: the picker is a filter, not history.
  useEffect(() => {
    const next = new URLSearchParams();
    if (period?.from) next.set('from', period.from);
    if (period?.to) next.set('to', period.to);
    setSp(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  useEffect(() => {
    let live = true;
    setSt(null);
    setErr(false);
    earnApi.getEarningsStatement(query)
      .then((r) => { if (live) setSt(r?.data ?? r); })
      .catch((e) => { if (live) { setErr(true); message.error(e.message); } });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const totals = st?.totals || {};
  const earned = Number(totals.entitled) || 0;
  const released = Number(totals.released) || 0;
  const paid = Number(totals.paid) || 0;
  const locked = Math.max(0, earned - released);
  const balance = released - paid;

  // The document is the same global letterhead the co-agency letters print on —
  // document_settings is company-wide, the endpoint just happens to be named for
  // letters. A missing settings row still yields a statement; the builder falls
  // back to the app's own CID mark.
  const openDoc = async () => {
    if (!st) return;
    setBusy(true);
    try {
      let settings = {};
      try {
        const r = await lettersApi.listCoAgencyLetterSettings();
        const v = r?.data ?? r;
        if (v && !Array.isArray(v)) settings = v;
      } catch (_) { /* letterhead is optional — the statement still prints */ }
      setDocHtml(buildStatementHtml(st, settings, new Date()));
    } finally {
      setBusy(false);
    }
  };

  const downloadCsv = async () => {
    try {
      const csv = await earnApi.getEarningsStatementCsv(query);
      const text = typeof csv === 'string' ? csv : csv?.data ?? '';
      const blob = new Blob([text], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${statementFileBase(st)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      message.error(e.message);
    }
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <AppBar
        back
        title="Commission statement"
        sub={st ? statementRef(st) : 'earnings statement'}
        right={
          <Dropdown
            trigger={['click']}
            disabled={!st || busy}
            menu={{
              items: [
                { key: 'pdf', label: 'View / print PDF' },
                { key: 'csv', label: 'Download CSV' },
              ],
              onClick: ({ key }) => (key === 'pdf' ? openDoc() : downloadCsv()),
            }}
          >
            <div
              role="button"
              aria-label="Download or print this statement"
              style={{ width: 34, height: 34, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: st && !busy ? 'pointer' : 'default', border: '1px solid var(--border)', background: 'var(--surface)', color: st && !busy ? 'var(--muted)' : 'var(--border)', flex: 'none' }}
            >
              {busy ? (
                <span style={{ width: 15, height: 15, borderRadius: '50%', border: '2px solid var(--border)', borderTopColor: 'var(--primary)', animation: 'spin .8s linear infinite' }} />
              ) : (
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 3v11" /><path d="M8 10.5l4 4 4-4" /><path d="M4 17v3h16v-3" />
                </svg>
              )}
            </div>
          </Dropdown>
        }
      />

      {/* no footer bar — the download menu lives in the header, so the last section
          carries the bottom inset itself */}
      <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: '12px 16px', paddingBottom: 'max(20px, calc(env(safe-area-inset-bottom) + 14px))', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <PeriodPicker value={period} onChange={setPeriod} />

        {err ? (
          <div style={{ color: 'var(--muted)', fontSize: 13, padding: '10px 2px' }}>Could not load this statement.</div>
        ) : !st ? (
          <Skeletons n={3} height={90} />
        ) : (
          <>
            {/* the statement head + reconciliation, on paper-white so it reads as a
                document even inside the app's dark theme */}
            <div style={{ background: '#fff', color: '#14181f', border: '1px solid #e6e8ee', borderRadius: 14, overflow: 'hidden' }}>
              <div style={{ padding: '14px 16px 12px', borderBottom: '2px solid #241854' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: '#241854', letterSpacing: '.02em' }}>COMMISSION STATEMENT</div>
                    <div style={{ fontSize: 11, color: '#5b6270', marginTop: 2 }}>{st.period?.label || 'All time'}</div>
                  </div>
                  <img src="/cid_fulllogo.png" alt="CID Realtors" style={{ height: 22, flex: 'none' }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginTop: 10, fontSize: 11, color: '#5b6270' }}>
                  <span style={{ minWidth: 0 }}>
                    <b style={{ color: '#14181f' }}>{st.agent?.name || '—'}</b>
                    {st.agent?.ren_no ? ` · REN ${String(st.agent.ren_no).replace(/^REN\s*/i, '')}` : ''}
                    {st.agent?.tier ? ` · ${st.agent.tier}% tier` : ''}
                  </span>
                  <span style={{ whiteSpace: 'nowrap' }}>{statementRef(st)}</span>
                </div>
              </div>

              <div style={{ padding: '12px 16px 14px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Recon label="Commission earned" value={RM(earned)} />
                <Recon label="Less — not yet released" value={`(${RM(locked)})`} dim />
                <Recon label="Released to your ledger" value={RM(released)} rule />
                <Recon label="Less — disbursed to you" value={`(${RM(paid)})`} dim />
                <Recon label="Balance held for you" value={RM(balance)} strong rule />
              </div>
            </div>

            <Section title="Commission by sale" count={st.commissions?.length || 0} total={RM0(earned)}>
              {st.commissions?.length ? st.commissions.map((c, i) => (
                <div key={i} style={rowCard}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontSize: 12, fontWeight: 600, fontFamily: 'ui-monospace,monospace' }}>{c.sale_ref}</span>
                    <span style={{ fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{RM0(c.entitled)}</span>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {docDate(c.sale_date)} · {shortAddress(c.property)}
                  </div>
                  <div style={{ display: 'flex', gap: 14, fontSize: 10.5, color: 'var(--muted)', fontVariantNumeric: 'tabular-nums' }}>
                    <span>released <b style={{ color: 'var(--text)' }}>{RM0(c.released)}</b></span>
                    <span>paid <b style={{ color: 'var(--text)' }}>{RM0(c.paid)}</b></span>
                  </div>
                </div>
              )) : <Empty>No commission was recorded in this period.</Empty>}
            </Section>

            {st.overrides?.length > 0 && (
              <Section title="Override income" count={st.overrides.length} total={RM0(totals.override)}>
                {st.overrides.map((o, i) => (
                  <div key={i} style={{ ...rowCard, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 600 }}>{o.from} · Gen {o.gen}</div>
                      <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'ui-monospace,monospace' }}>{o.sale_ref}</div>
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{RM0(o.amount)}</div>
                  </div>
                ))}
              </Section>
            )}

            <Section title="Disbursements" count={st.payouts?.length || 0} total={RM0(totals.payouts)}>
              {st.payouts?.length ? st.payouts.map((p, i) => (
                <div key={i} style={{ ...rowCard, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600 }}>{docDate(p.payout_date)}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>{p.cheque_number ? `Cheque ${p.cheque_number}` : 'No cheque reference'}</div>
                    {/* The payment voucher HQ issued for it. Tappable only when HQ
                        attached a copy — an old voucher is a number only. */}
                    {p.voucher_no && (p.voucher_has_file ? (
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => pv.open(p.id)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pv.open(p.id); } }}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, marginTop: 3, cursor: 'pointer', opacity: pv.busy === p.id ? 0.55 : 1 }}
                      >
                        <PdfMark />
                        <span style={{ color: 'var(--primary)', fontWeight: 600, fontFamily: 'ui-monospace,monospace' }}>{p.voucher_no}</span>
                        <span style={{ color: 'var(--primary)' }}>›</span>
                      </div>
                    ) : (
                      <div style={{ fontSize: 11.5, marginTop: 3, color: 'var(--muted)', fontFamily: 'ui-monospace,monospace' }}>{p.voucher_no}</div>
                    ))}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ok)', fontVariantNumeric: 'tabular-nums' }}>{RM0(p.amount)}</div>
                </div>
              )) : <Empty>No disbursement was made in this period.</Empty>}
            </Section>

            <div style={{ fontSize: 10.5, color: 'var(--muted)', lineHeight: 1.6, padding: '2px 2px 0' }}>
              Released commission is freed as HQ collects from the purchaser; the balance held is paid on the
              next payout run. Informational only — not a tax document.
            </div>
          </>
        )}
      </div>

      {pv.viewer}
      {docHtml && (
        <DocViewer
          originalOnly
          title={st ? `Statement · ${st.period?.label || 'All time'}` : 'Statement'}
          html={docHtml}
          onClose={() => setDocHtml(null)}
        />
      )}
    </div>
  );
}

const rowCard = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '11px 13px', display: 'flex', flexDirection: 'column', gap: 5 };

const Recon = ({ label, value, strong, dim, rule }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, padding: strong ? '8px 0 0' : '4px 0', borderTop: rule ? '1px solid #e6e8ee' : 'none', marginTop: rule ? 5 : 0 }}>
    <span style={{ fontSize: strong ? 12.5 : 12, fontWeight: strong ? 700 : 400, color: dim ? '#5b6270' : '#14181f' }}>{label}</span>
    <span style={{ fontSize: strong ? 15 : 12.5, fontWeight: strong ? 700 : 600, color: dim ? '#5b6270' : '#14181f', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{value}</span>
  </div>
);

const Section = ({ title, count, total, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, padding: '4px 2px 0' }}>
      <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.08em', color: 'var(--muted)', textTransform: 'uppercase' }}>
        {title}{count ? ` · ${count}` : ''}
      </span>
      <span style={{ fontSize: 11.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{total}</span>
    </div>
    {children}
  </div>
);

const Empty = ({ children }) => (
  <div style={{ fontSize: 12, color: 'var(--muted)', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 13px' }}>{children}</div>
);
